/**
 * Implementação de AnalyticsProvider usando Firebase Analytics
 *
 * Este provider implementa a interface AnalyticsProvider de @meiuca/analytics,
 * permitindo que o Analytics Service use o Firebase como backend de tracking.
 *
 * Dependency Inversion: Firebase depende de @meiuca/analytics (interface), não o contrário!
 */

import {
  getAnalytics,
  logEvent as firebaseLogEvent,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  setCurrentScreen as firebaseSetCurrentScreen,
  setConsent as firebaseSetConsent,
  isSupported,
  type Analytics
} from 'firebase/analytics';

import { getFirebaseApp, isFirebaseEnabled } from './firebase';
import type {
  AnalyticsProvider,
  EventProperties,
  UserProperties
} from '@meiuca/analytics';

/**
 * Opções de configuração do Firebase Analytics Provider
 */
export interface FirebaseAnalyticsOptions {
  /**
   * Modo de desenvolvimento (desabilita analytics automático em dev)
   */
  isDevelopment?: boolean;

  /**
   * Forçar habilitação mesmo em desenvolvimento
   */
  forceEnable?: boolean;
}

/**
 * Provider de Analytics usando Firebase Analytics
 */
export class FirebaseAnalyticsProvider implements AnalyticsProvider {
  private analytics: Analytics | null = null;
  private initialized = false;
  private enabled = false;

  async initialize(options: FirebaseAnalyticsOptions = {}): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!isFirebaseEnabled()) {
      console.warn('[FIREBASE_ANALYTICS_PROVIDER] Firebase not enabled, analytics disabled');
      this.initialized = true;
      return;
    }

    // Desabilita em desenvolvimento, a menos que forceEnable seja true
    const isDev = options.isDevelopment ?? false;
    if (isDev && !options.forceEnable) {
      console.log('[FIREBASE_ANALYTICS_PROVIDER] Analytics disabled in development mode');
      this.initialized = true;
      return;
    }

    try {
      // Verifica se Analytics é suportado no ambiente
      const supported = await isSupported();
      if (!supported) {
        console.warn('[FIREBASE_ANALYTICS_PROVIDER] Analytics not supported in this environment');
        this.initialized = true;
        return;
      }

      const app = getFirebaseApp();
      if (!app) {
        throw new Error('Firebase app not initialized');
      }

      this.analytics = getAnalytics(app);
      this.enabled = true;
      console.log('[FIREBASE_ANALYTICS_PROVIDER] Analytics initialized successfully');

      this.initialized = true;
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Failed to initialize:', error);
      this.initialized = true;
    }
  }

  logEvent(eventName: string, properties?: EventProperties): void {
    if (!this.enabled || !this.analytics) {
      return;
    }

    try {
      // On the web, GA4's native navigation event is `page_view` (not the app
      // `screen_view`). Remap so GA4's Pages/path/acquisition reports populate
      // with the real URL/title/referrer, while `screen_name` rides along as a
      // param. Mixpanel keeps `screen_view` via its own provider.
      if (eventName === 'screen_view') {
        firebaseLogEvent(this.analytics, 'page_view', {
          ...(properties as Record<string, any>),
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: document.referrer || undefined,
        });
        return;
      }

      firebaseLogEvent(this.analytics, eventName, properties as Record<string, any>);
    } catch (error) {
      console.error(`[FIREBASE_ANALYTICS_PROVIDER] Error logging event ${eventName}:`, error);
    }
  }

  setUserId(userId: string | null): void {
    if (!this.enabled || !this.analytics) {
      return;
    }

    try {
      firebaseSetUserId(this.analytics, userId);
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Error setting user ID:', error);
    }
  }

  setUserProperties(properties: UserProperties): void {
    if (!this.enabled || !this.analytics) {
      return;
    }

    try {
      firebaseSetUserProperties(this.analytics, properties as Record<string, any>);
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Error setting user properties:', error);
    }
  }

  logPageView(pageName: string, pageLocation?: string): void {
    if (!this.enabled || !this.analytics) {
      return;
    }

    try {
      firebaseLogEvent(this.analytics, 'page_view', {
        page_title: pageName,
        page_location: pageLocation || window.location.href
      });
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Error logging page view:', error);
    }
  }

  setCurrentScreen(screenName: string): void {
    if (!this.enabled || !this.analytics) {
      return;
    }

    try {
      firebaseSetCurrentScreen(this.analytics, screenName);
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Error setting current screen:', error);
    }
  }

  /**
   * Consent Mode: updates analytics_storage consent.
   * Applied globally (not per Firebase app instance).
   */
  setConsent(granted: boolean): void {
    try {
      firebaseSetConsent({
        analytics_storage: granted ? 'granted' : 'denied',
      });
    } catch (error) {
      console.error('[FIREBASE_ANALYTICS_PROVIDER] Error setting consent:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
